import React, { useEffect, useMemo, useState } from 'react';
import {
  TRANSACTIONS_AGGREGATE_BY_TITLE,
  TRANSACTIONS_AGGREGATE_BY_TYPE_ENUM,
  TRANSACTIONS_AGGREGATE_COMPARE_TO_ENUM,
  TRANSACTIONS_AGGREGATE_COMPARE_TO_OPTIONS,
  TRANSACTIONS_AGGREGATE_COMPARE_TO_RESULT_KEYS_ENUM,
  TRANSACTIONS_AGGREGATE_COMPARE_TO_TITLES_ENUM,
  TRANSACTIONS_AGGREGATE_FIELDS_ENUM,
  TRANSACTIONS_AGGREGATE_GRAPH_ADJUSTMENT_OPTIONS,
  TRANSACTIONS_AGGREGATE_GRAPH_BY_OPTIONS,
  TRANSACTIONS_STACK_BY_OPTIONS,
} from '../../constants';

import { MaGraph } from '../../../../../../components/Graphs/MaGraph';
import { getGraphLayout } from '../../../../../../components/Graphs/MaGraph/helpers';
import { titleize } from '../../../../../../utils/helpers';
import { useGetChartData } from './hooks/useGetChartData';

const SESSION_STORAGE_KEY = 'MaOverviewTransactionsChart';
const getInitialChartSettings = () => {
  const sessionSettings = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (sessionSettings) {
    return JSON.parse(sessionSettings);
  }

  return {
    stackBy: 'NONE', // default value must be non-null otherwise options wont render
    aggregateBy: TRANSACTIONS_AGGREGATE_BY_TYPE_ENUM.COUNTRY,
    compareTo: TRANSACTIONS_AGGREGATE_COMPARE_TO_ENUM.RESERVES,
  };
};

export const TransactionsChart = () => {
  const {
    stackBy: defaultStackBy,
    aggregateBy: defaultAggregateBy,
    compareTo: defaultCompareTo,
  } = getInitialChartSettings();

  const [stackBy, setStackBy] = useState(defaultStackBy);
  const [aggregateBy, setAggregateBy] = useState(defaultAggregateBy);
  const [compareTo, setCompareTo] = useState(defaultCompareTo);
  const [adjustments, setAdjustments] = useState({});

  const fields = useMemo(
    () => [
      TRANSACTIONS_AGGREGATE_COMPARE_TO_RESULT_KEYS_ENUM[compareTo],
      TRANSACTIONS_AGGREGATE_FIELDS_ENUM.VALUATIONS,
    ],
    [compareTo],
  );

  useEffect(() => {
    // Save the current state to sessionStorage
    sessionStorage.setItem(
      SESSION_STORAGE_KEY,
      JSON.stringify({ stackBy, aggregateBy, compareTo }),
    );
  }, [stackBy, aggregateBy, compareTo]);

  const xConfig = {
    value: aggregateBy,
    setValue: setAggregateBy,
    options: TRANSACTIONS_AGGREGATE_GRAPH_BY_OPTIONS,
  };
  const yConfig = {
    value: compareTo,
    setValue: setCompareTo,
    options: TRANSACTIONS_AGGREGATE_COMPARE_TO_OPTIONS,
  };

  const stackByConfig = {
    value: stackBy,
    setValue: setStackBy,
    options: TRANSACTIONS_STACK_BY_OPTIONS,
  };

  const adjustmentsConfig = {
    values: adjustments,
    setValues: setAdjustments,
    options: TRANSACTIONS_AGGREGATE_GRAPH_ADJUSTMENT_OPTIONS,
  };

  const yAxisTitle = TRANSACTIONS_AGGREGATE_BY_TITLE;
  const altYAxisTitle = TRANSACTIONS_AGGREGATE_COMPARE_TO_TITLES_ENUM[compareTo];
  const graphLayout = getGraphLayout({
    showLegend: true,
    yAxisTitle: yAxisTitle,
    altYAxisTitle: altYAxisTitle,
  });

  const { data, loading, error } = useGetChartData({
    aggregateBy,
    stackBy,
    compareTo,
    adjustments,
    fields,
  });

  if (error) return null;

  return (
    <MaGraph
      data={data}
      title={`NPV10 Valuation by ${titleize(aggregateBy)}`}
      loading={loading}
      xConfig={xConfig}
      yConfig={yConfig}
      stackByConfig={stackByConfig}
      adjustmentsConfig={adjustmentsConfig}
      graphLayout={graphLayout}
      maxXAxisTicks={aggregateBy === TRANSACTIONS_AGGREGATE_BY_TYPE_ENUM.YEAR ? 0 : 50}
    />
  );
};
